import type { LandmarkDef } from './common';
import { landmarkModel, windowGlass } from './model';

/**
 * landmarks/nyc.ts — New York's hero landmarks, all hand-built low-poly Blender
 * models (glTF in public/models/landmarks/). This file is just the registry +
 * placement; see Landmarks.buildLandmark for how 'side' / 'span' / 'finish'
 * position + orient each model. Drive-through gateways ('span') scale by WIDTH so
 * the opening clears the road (2*halfWidth + margin); the rest scale by height.
 */
// GS6 emissive discipline: these models bake their 'win' slabs with a PALE
// CREAM base + hot emissive — blown white boxes (the M43 regression,
// QA-confirmed). windowGlass() retrofits the Apollo recipe (dark glass pane,
// warm glow); night city, so the glow stays alive at ~0.8.
export const NYC_LANDMARKS: Record<string, LandmarkDef> = {
  apollo: { build: () => landmarkModel('models/landmarks/apollo.glb', { height: 18 }), placement: 'side', offset: 8 },
  hamiltonGrange: { build: () => landmarkModel('models/landmarks/hamilton_grange.glb', { height: 16, ...windowGlass('win', 0.85) }), placement: 'side', offset: 9 },
  langstonHughes: { build: () => landmarkModel('models/landmarks/langston_hughes.glb', { height: 18, ...windowGlass('win', 0.8) }), placement: 'side', offset: 7 },
  belvedereCastle: { build: () => landmarkModel('models/landmarks/belvedere_castle.glb', { height: 24, ...windowGlass('win', 0.85) }), placement: 'side', offset: 14 },
  bowBridge: { build: (hw) => landmarkModel('models/landmarks/bow_bridge.glb', { width: 2 * hw + 22 }), placement: 'span' },
  // GS-NYC round: rebuilt from the M13 procedural spec (git 949ecbc) after QA
  // found four floating planks over a drum where the Angel of the Waters should
  // be. Terrace/basin were pulled in from M13's r18/r12 — at height 13 that plan
  // made the figure unreadable from the road. 'water' and 'bronze' are both in
  // texturing.ts's SKIP list, so the pool stays flat blue and the angel verdigris.
  // height 13 -> 17: the Central Park zone puts a hill right behind this offset,
  // and at 13 the terrain swallowed the basin so only the angel's wings cleared
  // the crest (QA). M98's hill clamp keeps domes off the ROAD, not off a side
  // landmark, so the model has to out-reach the hill instead.
  bethesdaFountain: { build: () => landmarkModel('models/landmarks/bethesda_fountain.glb', { height: 17 }), placement: 'side', offset: 16 },
  // The full-height ES_win floodlight strips blew to pink-white neon tubes;
  // halved + warmed to gold so the limestone albedo carries the light (QA).
  empireState: {
    build: () =>
      landmarkModel('models/landmarks/empire_state.glb', {
        height: 96,
        emissiveScaleMats: { ES_win: 0.5, ES_crown: 0.5 },
        emissiveTintMats: { ES_win: 0xffb054, ES_crown: 0xffb054 },
      }),
    placement: 'side',
    // nyc-park round (owner: "the Empire State isn't visible, it's hidden behind
    // other skyscrapers — bring it to the edge of the road"). MEASURED in the
    // live scene: the model's base spans ±21 about its anchor, so at offset 26 its
    // inner face already stood only 4.7 u off the guardrail — the tower was never
    // far away, it was OCCLUDED (generic Broadway towers spawn at lateral 20-42,
    // 22-55 tall, right in front of it). So the real fix is the forecourt clearing
    // in scenery/zones.ts; this is the last honest unit of prominence available —
    // offset 24 puts the inner face 2.7 u outboard of the rail, and anything lower
    // pushes a 96 u tower through the guardrail itself.
    offset: 24,
  },
  // GS-NYC round, finishing the M43 TODO: the crown's seven tiers were solid
  // silver slabs — no triangular lights, no radiating sunburst, which is the only
  // thing that makes this tower recognisable — and the shaft was brick RED. It is
  // rebuilt with a real fan of triangular lights per tier and a pale steel-grey
  // shaft on the CONCRETE triplanar role ('concShaft'; the base is authored high
  // at 0.80 because that atlas multiplies diffuse by ~0.43). windowGlass joins the
  // rest of this file's models, but at 0.45 rather than the 0.8 used elsewhere:
  // these strips run floor-to-floor, so at 0.8 the emissive area beat the masonry
  // and the tower read solid gold — the exact opposite of a pale steel-grey shaft.
  chrysler: {
    build: () =>
      landmarkModel('models/landmarks/chrysler.glb', { height: 88, ...windowGlass('win', 0.45) }),
    placement: 'side',
    offset: 22,
  },
  // M97: `ahead` counts past the COURSE END now — the tower's face clears the
  // end barrier (measured half-depth 5.5) so finished cars never coast through it.
  nyeBall: { build: () => landmarkModel('models/landmarks/nye_ball.glb', { height: 38 }), placement: 'finish', ahead: 24 },
};
